import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";
import "./userMenu.css";
import { request } from "utils/request";
import { useDispatch, useSelector } from "react-redux";
import {
  Person,
  CreditCard,
  PatchQuestion,
  BoxArrowInRight,
} from "react-bootstrap-icons";
import { Badge, Button } from "react-bootstrap";
import { logout } from "app/containers/Login/slice";
import { fetchUserProfile } from "app/containers/UserInfo/slice";
import {
  daysUntilTheEndOfTheMonth,
  getDiffDaysSinceToday,
} from "utils/helperFunctions/daysCalculations";
import UpgradePlanModal from "./UpgradePlanModal";
import { useLoadingContext } from "app/contexts/LoadingContext";

const FREE_TIER_REPORTS_QUOTA = 5;

const UserMenu = ({ show, onClose, profileImage, defaultAvatar }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { profile } = useSelector((state) => state.userProfile);
  const [showUpgradePlanModal, setShowUpgradePlanModal] = useState(false);
  const { showMaskedLoading, hideMaskedLoading } = useLoadingContext();

  const debouncedFetchProfile = useDebouncedCallback(
    () => {
      dispatch(fetchUserProfile());
    },
    750 // 0.75 second debounce delay
  );

  useEffect(() => {
    if (show) {
      debouncedFetchProfile();
    }
  }, [show, debouncedFetchProfile]);

  const handleSubscription = async () => {
    try {
      showMaskedLoading();
      const response = await request(
        `${process.env.REACT_APP_API_URL}/payments/customer-portal/`,
        {
          method: "POST",
          body: JSON.stringify({
            redirect_url: `${process.env.REACT_APP_URL}`,
          }),
        }
      );
      window.location.href = response.url; // redirect to the customer portal
      onClose();
    } catch (error) {
      // Error handled by finally block
      hideMaskedLoading();
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
    onClose();
  };

  if (!show || !profile) return null;

  return (
    <div className="user-menu">
      <div className="user-menu-items">
        <div className="user-menu-item">
          <div className="user-menu-item-content">
            <div className="user-menu-item-content-profile">
              <h5>
                {profile.first_name} {profile.last_name}
              </h5>
              <Badge
                className={`badge-${
                  profile?.subscription?.tier?.toLowerCase() || "free"
                }`}
              >
                {profile?.subscription?.tier || "Free"}
              </Badge>
            </div>
            <span className="user-menu-item-email">
              {profile.email || "No email found"}
            </span>
            {profile.subscription?.tier === "Free" ? (
              <div className="user-menu-item-content-free-tier">
                <div className="user-menu-item-content-free-tier-used-reports">
                  <h6>Reports used</h6>
                  <span>
                    {profile.monthly_compiled_reports} /{" "}
                    {FREE_TIER_REPORTS_QUOTA}
                  </span>
                </div>
                <span className="user-menu-item-content-free-tier-reset-in">
                  Reset in {daysUntilTheEndOfTheMonth()} days
                </span>
                <Button
                  variant="primary"
                  onClick={() => setShowUpgradePlanModal(true)}
                >
                  Upgrade for Unlimited
                </Button>
              </div>
            ) : profile.subscription?.trial_end ? (
              <div className="user-menu-item-content-free-tier">
                <span className="user-menu-item-content-free-tier-reset-in">
                  Free trial ends in{" "}
                  {getDiffDaysSinceToday(profile.subscription.trial_end)} days
                </span>
              </div>
            ) : null}
          </div>
        </div>
        <hr className="divider" />
        <Link to="/user-info" className="user-menu-item" onClick={onClose}>
          <Person />
          <span>User Information</span>
        </Link>
        <button className="user-menu-item" onClick={handleSubscription}>
          <CreditCard />
          <span>Subscription</span>
        </button>
        <Link to="/contact-us" className="user-menu-item" onClick={onClose}>
          <PatchQuestion />
          <span>Contact for Help</span>
        </Link>
        <hr className="divider" />
        <button className="user-menu-item logout" onClick={handleLogout}>
          <BoxArrowInRight />
          <span>Logout</span>
        </button>
      </div>
      <UpgradePlanModal
        open={showUpgradePlanModal}
        onClose={() => setShowUpgradePlanModal(false)}
      />
    </div>
  );
};

export default UserMenu;
